import { clone } from 'lodash';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import {
  ClientWindowSticker,
  CLIENT_WINDOW_STICKER_TYPE,
} from 'src/app/model/product/windowsticker/client-windowsticker';
import CustomWindowsticker from 'src/app/model/product/windowsticker/custom-windowsticker';
import { isStandardStickerPart } from 'src/app/model/product/windowsticker/standard-sticker-part';
import { isStickerPart } from 'src/app/model/product/windowsticker/sticker-part';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import { Project } from 'src/app/model/project/project';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';

export class CreateViewProjectHelper {
  private constructor() {}

  static cloneProject(project: Project): Project {
    if (project == null) {
      return null;
    }

    const toReturn: Project = clone(project);
    toReturn.products = [];
    if (CollectionHelper.isPopulated(project.products)) {
      project.products.forEach((p) =>
        this.doCloneProduct(p, toReturn.products)
      );
    }
    return toReturn;
  }

  private static doCloneProduct(product: Product, products: Product[]): void {
    if (product == null) {
      return;
    }
    switch (product.type) {
      case ProductTypes.WINDOWSTICKER.value:
      case ProductTypes.CUSTOMWINDOWSTICKER.value:
        throw new Error(
          'New client projects should not have regular WindowStickers'
        );
      case CLIENT_WINDOW_STICKER_TYPE:
        this.doCloneClientWindowSticker(<ClientWindowSticker>product, products);
        break;
      default:
        products.push(clone(product));
    }
  }

  private static doCloneClientWindowSticker(
    windowSticker: ClientWindowSticker,
    products: Product[]
  ): void {
    if (!CollectionHelper.isPopulated(windowSticker.clientStickerParts)) {
      return;
    }

    let standardWindowSticker: Windowsticker = null;
    let customWindowSticker: CustomWindowsticker = null;

    windowSticker.clientStickerParts.forEach((p) => {
      if (p != null && p.instance != null) {
        if (isStandardStickerPart(p.instance)) {
          if (standardWindowSticker == null) {
            standardWindowSticker = new Windowsticker();
            standardWindowSticker.standardStickerParts = [];
            this.copyProductFields(standardWindowSticker, windowSticker);
          }
          standardWindowSticker.standardStickerParts.push(p.instance);
        } else if (isStickerPart(p.instance)) {
          if (customWindowSticker == null) {
            customWindowSticker = new CustomWindowsticker();
            customWindowSticker.stickerParts = [];
            this.copyProductFields(customWindowSticker, windowSticker);
          }
          customWindowSticker.stickerParts.push(p.instance);
        }
      }
    });

    if (standardWindowSticker != null) {
      products.push(standardWindowSticker);
    }
    if (customWindowSticker != null) {
      products.push(customWindowSticker);
    }
  }

  private static copyProductFields(
    windowSticker: Windowsticker,
    clientWindowSticker: ClientWindowSticker
  ): void {
    windowSticker.foil = clientWindowSticker.foil;
    windowSticker.laminate = clientWindowSticker.laminate;
    windowSticker.newDesign = clientWindowSticker.newDesign;
    windowSticker.generalExtraInfo = clientWindowSticker.generalExtraInfo;
    windowSticker.productPhoneNumbers = clientWindowSticker.productPhoneNumbers;
    windowSticker.title = clientWindowSticker.title;
    windowSticker.text = clientWindowSticker.text;
    windowSticker.productAssets = clientWindowSticker.productAssets;
    windowSticker.immoscoopLogo = clientWindowSticker.immoscoopLogo;
    windowSticker.spottoLogo = clientWindowSticker.spottoLogo;
    windowSticker.productPlacement = clone(
      clientWindowSticker.productPlacement
    );
  }

  public static updateClientSelectedCompanyAfterSubmit(
    project: Project,
    clientCompanySelectService: ClientCompanySelectService
  ): void {
    if (
      project != null &&
      CollectionHelper.isPopulated(project.projectContacts)
    ) {
      const projectContact = project.projectContacts.find(
        (pc) => pc.company != null && pc.company.id != null
      );
      if (projectContact != null) {
        clientCompanySelectService.trySelectCompany(projectContact.company.id);
      }
    }
  }
}
